import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "../axios";
import { advance, edgeOpacity } from "../utils/lineMapProgress";
import { delayBadge } from "../utils/delayBadge";
import { laneOf, expressOf, dirOf } from "../utils/lineMapLanes";
import { stackOffsets } from "../utils/lineMapStack";
import { viaOnly } from "../utils/lineMapTrip";
import LineDiagramView, { DiagramLegend } from "./LineDiagramView";
import { routeScale } from "../utils/routeFit";
import "../styles/analysis.css";

// データの取り直し。**bot が 30 秒ごとに位置を吐き出すのに合わせる。**
// 60 秒だと、せっかく早く届いた観測が画面に出るまで平均 30 秒待つ。
// 入口は super_admin だけなので、倍にしても負荷の心配は無い
const AUTO_INTERVAL = 30000;
const TICK = 250;              // 位置の進め方（CSS の transition と揃える）

// 観測が更新されて位置がこれ以上ズレたら、滑らせずに差し替える（位置番号）
const SWAP_THRESHOLD = 0.2;
// 差し替えの片道（ms）。**CSS の opacity の遷移と揃える**
const SWAP_MS = 250;
// 確定・遅れの増減を知らせる一瞬（ms）。**CSS の animation と揃える**
const FLASH_MS = 1100;

// 早送りの段。**1 は等速。**押すごとに次へ回り、最後でまた等速に戻る。
// 1 分進むごとにデータを取り直すので、上げすぎるとサーバを叩きすぎる
// （×30 で 2 秒に 1 回）
const SPEEDS = [4, 10, 30];

// 1 停留所ぶんの高さと、札から三角までの距離。**CSS と揃えること**
const ROW = 80;
const CARD = 46;

/**
 * 横に並ぶもの（線の中心からの距離）。**出どころはここ 1 か所。**
 *
 * CSS へは `.lm-line` の custom property で渡す。バスの横位置だけは JS で
 * 出して transform に入れる — **線を移るときに滑らせたい**ので、CSS の
 * 変数越しではなく素の値にしておく。
 *
 * 内外は地形に合わせる（海岸通りは海側なので支線の外、29 番の名島まわりは
 * 内陸側なので内）。**支線はこれから増える**前提で、増えたらここに足す。
 */
const LANES = {
  railBus: 22,    // 本線のバス（上りは左、下りは右）
  bypass: 86,     // 都市高速の外縁（線は 7px）

  // 支線の車線。**同じ側で縦に重なる枝は 1 つ外へ**（下原と高美台）。
  // 支線が増えたらここに足す。
  //
  // 2 番目が 500 なのは、**香椎浜の海岸通り回り（500）が 27.08 で終わり、
  // 照葉が 27.428 から始まる**ため — 縦に重ならないので同じ車線に入る。
  // 1 段内へ寄せたぶん、図が左右あわせて 380px 狭くなる
  //
  //   380  香椎浜・三苫・高美台・千早駅（左右とも）
  //   500  照葉（アイランドシティ）・下原
  //   640  雁の巣
  spur: [380, 500, 640],
  // 同じ区間の別の道は、その支線の内と外へこの幅だけ離す。
  //
  // **アイランドシティのまわりは 3 本並ぶ。**内から順に
  //
  //   inner  香椎浜車庫への渡り・センターマークス前
  //   outer  福岡市総合体育館 → アイランドアイ前 → ベジフル（島の西側）
  //   far    香椎照葉五丁目 → 福岡市総合体育館西 / みなと香椎まわり
  //
  // 雁の巣への橋は雁の巣の枝の outer（760）。島の 3 本（620・740）とは
  // 別の車線なので、縦に重なっても線は分かれる
  altGap: 120,
  arrow: 14,      // 枠の左端から矢印の中心まで（枠線 2 + 余白 7 + 矢印の半分）
};

/** その車線の支線の外縁（1 始まり） */
/**
 * 同じ区間の別の道の外縁。**内・外・その外**の 3 本。
 *
 * アイランドシティのまわりは、節 27.6〜29 に道が 3 本並ぶ。
 * **重ねると別の道が同じ線に見えて、通らない停留所を通っているように
 * 読める。**
 */
const ALT_STEP = { inner: -1, outer: 1, far: 2 };

function altOutOf(out, lane) {
  return out + LANES.altGap * (ALT_STEP[lane] ?? 1);
}

function spurOut(lane) {
  return LANES.spur[(lane || 1) - 1] ?? LANES.spur[LANES.spur.length - 1];
}

/** 支線ごとの横位置。**CSS へはこの支線の枠に custom property で渡す** */
function spurVars(lane) {
  const out = spurOut(lane);

  return {
    "--lm-spur-out": `${out}px`,
    "--lm-alt-in": `${out - LANES.altGap}px`,
    "--lm-alt-out": `${out + LANES.altGap}px`,
    "--lm-alt-far": `${out + LANES.altGap * 2}px`,
  };
}

// 線の脇に置くものが、線からどれだけ外へ出るか。バス停名（約 105px）と
// バスの枠（約 140px のうち矢印より外の 126px）の大きいほう
const REACH = 130;

/**
 * 線の縦の長さ（節の番号で）。**枝は節より下へ伸びる** —
 * 大蔵は 39、西鉄三苫駅は 38 で、節は 34 までしか無い。
 */
function maxExtent(nodes, spurs) {
  let max = nodes.length - 1;
  (spurs || []).forEach((spur) => {
    max = Math.max(max, spur.to, ...spur.stops.map((x) => x.node));
    (spur.alternates || []).forEach((alt) => {
      max = Math.max(max, alt.from, alt.to, ...alt.stops.map((x) => x.node));
    });
  });

  return max;
}

/**
 * ランプの腕。**降りた先まで引く。**
 *
 * 降りた次が節のランプは内へ（帯まで）、支線の停留所のランプはその支線の
 * 線まで。支線が本線の反対側にあるときは**本線を横切る**（実際に都市高速は
 * 3 号線をまたいでいる）。
 *
 * 返すのは帯からの距離（px）。CSS 変数で渡すと、支線ごとに車線が違うので
 * 足りない。
 */
function rampArm(ramp, spurs) {
  if (ramp.joins) {
    return { left: 7, width: LANES.bypass - 7 };
  }

  const spur = (spurs || []).find((x) => x.key === ramp.spur);
  const out = spurOut(spur?.lane);

  if ((spur?.side ?? "right") === "right") {
    return { left: LANES.bypass, width: out - 5 - LANES.bypass };
  }

  return { left: -(out - 5), width: out - 5 + LANES.bypass };
}

/**
 * 支線が繋がるところの腕。**帯まで、あるいは親の支線まで。**
 *
 * 照葉はイオンモール香椎浜（香椎浜の支線）から分かれるので、帯ではなく
 * 香椎浜の線まで引く。位置は車線から出す — **支線ごとに違うので CSS の
 * 変数では足りない。**
 *
 * 返すのは帯からの距離（px）と長さ。
 */
function joinArm(spur, spurs) {
  const out = spurOut(spur.lane);
  const left = spur.side === "left";

  if (spur.host) {
    const host = (spurs || []).find((x) => x.key === spur.host);
    const hostOut = spurOut(host?.lane);
    // 内側の線の外縁から、外側の線の内縁まで
    const near = Math.min(hostOut, out) - 5;
    const far = Math.max(hostOut, out);

    return { x: left ? -far : near, w: far - near };
  }

  // 帯まで（本線と合流する枝）
  return { x: left ? -out : 7, w: out - 7 };
}

/**
 * 別の道が分かれる／戻るところの腕。**繋がる先は 3 通り。**
 *
 *   本線     帯まで（29 番は名島へ出る）
 *   ほかの支線 その支線の線まで（21B のループは照葉の福岡市総合体育館へ）
 *   本道     自分の支線の線まで（香椎浜の海岸通り回り）
 *
 * 位置は車線から出す — **支線ごとに違うので CSS の変数では足りない。**
 */
function forkArm(spur, alt, end, spurs) {
  const own = spurOut(spur.lane);
  const altOut = altOutOf(own, alt.lane);
  const left = spur.side === "left";

  if (end.rail) {
    return { x: left ? -altOut : 7, w: altOut - 7 };
  }

  const host = end.host ? (spurs || []).find((x) => x.key === end.host) : null;
  // **端が別の道の上にあることがある。**みなと香椎まわりはアイランドアイ前
  // （21B のループ＝外の道）に架かるので、本道まで引くと通らない線まで
  // 届いてしまう
  const target = end.alt
    ? altOutOf(spurOut(host ? host.lane : spur.lane), end.alt)
    : spurOut(host ? host.lane : spur.lane);
  // 内側の線の外縁から、外側の線の内縁まで（角を埋める）
  const near = Math.min(target, altOut) - 5;
  const far = Math.max(target, altOut);

  return { x: left ? -far : near, w: far - near };
}

/** ランプ名を置くところ（帯からの距離）。腕を引いた側に添える */
function rampLabel(ramp, spurs) {
  if (ramp.joins) return 20;

  const spur = (spurs || []).find((x) => x.key === ramp.spur);
  if ((spur?.side ?? "right") === "right") return LANES.bypass + 24;

  // 反対側は支線の線のすぐ内へ（そこから名前が本線のほうへ伸びる）
  return -(spurOut(spur?.lane) - 12);
}

/**
 * 線を置く幅と、帯を置く位置。**その線に実際にある枝から出す。**
 *
 * 狭い画面では横に溢れる。溢れた左側は、幅を決めて折りたたみの中に入れ
 * ないとスクロールで辿れない（ブラウザは左へ溢れたぶんをスクロール範囲に
 * しない）。
 *
 * **左右で別に数える。**帯を真ん中（50%）に決め打つと、遠いほうに合わせて
 * 対称に取ることになり、**入るはずの画面でもスクロールが出る**（高美台は
 * 左だけなので、右に同じ幅は要らない）。
 *
 * @returns {{width: number, axis: number}}
 */
function lineBox(bypasses, spurs) {
  // 本線のバスは左右どちらにも出る
  let left = LANES.railBus + REACH;
  let right = LANES.railBus + REACH;

  // 線 1 本ぶん。o だけ離れた線の外へ REACH、内へは残りぶん食い込む
  const track = (o, side) => {
    if (side === "left") {
      left = Math.max(left, o + REACH);
      right = Math.max(right, REACH - o);
    } else {
      right = Math.max(right, o + REACH);
      left = Math.max(left, REACH - o);
    }
  };

  // 都市高速は右に 1 本だけ
  if ((bypasses || []).length > 0) track(LANES.bypass, "right");

  // ランプ名は腕の先に添える（反対側へ横切るものがある）
  (bypasses || []).forEach((b) => {
    (b.ramps || []).forEach((r) => {
      const at = rampLabel(r, spurs);
      track(Math.abs(at), at < 0 ? "left" : "right");
    });
  });

  (spurs || []).forEach((spur) => {
    const out = spurOut(spur.lane);
    track(out, spur.side);
    (spur.alternates || []).forEach((alt) => {
      track(altOutOf(out, alt.lane), spur.side);
    });
  });

  return { width: Math.ceil(left + right), axis: Math.ceil(left) };
}

// CSS が読む横位置。**バスの横位置は JS が transform に入れる**ので、
// 矢印の出しろ（LANES.arrow）はここには要らない
const LANE_VARS = {
  "--lm-bypass-out": `${LANES.bypass}px`,

};

// 線を移るときに滑る時間（ms）。**CSS の .is-lane と揃えること。**
// 位置を進める 250ms のままだと 500px を一瞬で飛ぶので、そこだけ長くする
const LANE_MS = 700;

/**
 * 本線を外れた線の中心（線の太さの半分だけ内へ寄せたところ）。
 * 本線なら null。
 */
function laneCenter(lane, side, altLane, spurLane) {
  const out = spurOut(spurLane);
  const center = {
    bypass: LANES.bypass - 3.5,
    spur: out - 2.5,
    alt: altOutOf(out, altLane) - 2.5,
  }[lane];
  if (center === undefined) return null;

  // 都市高速は右に 1 本だけ（道が 1 本なので向きで分けない）
  return lane !== "bypass" && side === "left" ? -center : center;
}

/**
 * バスの横位置（transform の translateX に入れる文字列）。
 *
 * ## 左側通行に合わせる。**線を外れても同じ**
 *
 * 下りは進行方向の左が画面の右になるので枠を線の右へ、上りは左へ出す。
 * 本線の帯でそうしているので、都市高速や支線でも揃える。向きで並べ方が
 * 変わるのは `.lm-mover--right/left` の指定（推測バッジと矢印の側）。
 *
 * ## 線を外れたら矢印を線の真上に
 *
 * 矢印がその 1 台の居場所を指しているので、そこが線から外れていると位置が
 * 読めない。下りは枠の左端から、上りは右端から `arrow` ぶんのところに
 * 矢印が来る（枠の中身を反転させてあるので左右対称）。
 *
 * 上りは枠の右端を合わせるので、幅のぶん（100%）戻す。
 */
function busX(lane, side, altLane, spurLane, dir) {
  const center = laneCenter(lane, side, altLane, spurLane);

  // 本線は帯の脇。線の上ではないので矢印は合わせない
  if (center === null) {
    return dir === "forward"
      ? `${LANES.railBus}px`
      : `calc(-${LANES.railBus}px - 100%)`;
  }

  return dir === "forward"
    ? `${center - LANES.arrow}px`
    : `calc(${center + LANES.arrow}px - 100%)`;
}

// 向きごとの色と矢印。**線は 1 本で、向きは色と矢印で分ける。**
const DIR_STYLE = {
  forward: { color: "#0f9d58", arrow: "▼" },
  backward: { color: "#1a73e8", arrow: "▲" },
};

/**
 * 路線図 View。
 *
 * ## 縦に積む。上下でバス停を共用する
 *
 * 向きごとに線を引くと同じ停留所が 2 回出てきて読み取りにくい。停留所は
 * 1 本の並びにして、向きは色と三角で分ける。
 *
 * ## 左右は左側通行に合わせる
 *
 * 下り（並びをたどる向き＝画面の下へ）は進行方向の左が画面の右になる。
 * 上りはその逆。**帯もバスもこの並びに揃える。**
 *
 * ## 区間を滑る位置は推定。**観測と見分けが付くようにする**
 *
 * 置ける事実は「その時刻にその停留所にいた」だけ。そこから先は時刻表の
 * 所要で進めた推定で、遅れが読めていない便ほど外れる。進め方は
 * `utils/lineMapProgress.js`。**塗り＝停留所、輪郭＝区間**で見分ける。
 */
export default function AnalysisLineMapPage() {
  const { key } = useParams();
  const navigate = useNavigate();
  // **既定はアイランドシティ営業所管区**（手で描いた図）。節番号の線
  // （ohori-*）は 2026-09-23 にお蔵入りした（config/linemap.php の shelved）
  const lineKey = key || "island";

  const [params, setParams] = useSearchParams();
  // **時刻を指定して眺められる。**始発前・終バス後は走っている便が無いので、
  // いまの時刻だけでは画面を確かめられない。過去日を見るときも同じ口を使う
  const at = params.get("at");
  const date = params.get("date");

  const query = useMemo(() => {
    const q = new URLSearchParams();
    if (at) q.set("at", at);
    if (date) q.set("date", date);
    const s = q.toString();
    return s ? `?${s}` : "";
  }, [at, date]);

  const [lines, setLines] = useState([]);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(() => Date.now());
  // タップで開く詳細。**車号と向きで指す**（位置は動くので位置では指せない）
  const [selected, setSelected] = useState(null);
  // 早送りの倍率（1 が等速）。**指定した時刻からでないと始められない**
  const [speed, setSpeed] = useState(1);
  const fetchedAt = useRef(Date.now());
  // 横スクロールの入れもの。**初めは本線が見えるところへ寄せる**
  const scroller = useRef(null);
  const centered = useRef(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`/analysis/line-maps/${lineKey}${query}`);
      setData(res.data);
      fetchedAt.current = Date.now();
      setError("");
    } catch (e) {
      setError(
        e.response?.status === 404
          ? "その路線図はありません。"
          : "読み込めませんでした。"
      );
    } finally {
      setLoading(false);
    }
  }, [lineKey, query]);

  useEffect(() => {
    axios
      .get("/analysis/line-maps")
      .then((res) => setLines(res.data))
      .catch(() => setLines([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    load();
    // **時刻を指定しているあいだは要らない。**下の effect が 1 分ごとに
    // 時刻を進め、そのたびに取り直している
    if (at) return undefined;
    const id = setInterval(load, AUTO_INTERVAL);
    return () => clearInterval(id);
  }, [load, at]);

  // **見ている時刻を進める。**時刻を指定しているあいだも時計は止めない —
  // 1 分経ったら 1 分進める。早送りのときはその倍率ぶん速く進む。
  //
  // 1 分進むごとに取り直すので、出ているのは常にその時刻のデータ
  // （当てた絵ではない）。
  //
  // 1 回ずつ setTimeout を張り直す。at が動くとこの effect も回るので、
  // それが次の 1 分になる
  useEffect(() => {
    if (! at) return undefined;

    const id = setTimeout(() => {
      const next = new Date(at.replace(" ", "T"));
      next.setMinutes(next.getMinutes() + 1);

      // **いまに追いついたら止める。**追い越すと未来を見ることになり、
      // 観測が 1 件も無い（時刻表だけの絵）になる
      if (next.getTime() > Date.now()) {
        setSpeed(1);
        setParams(
          (p) => {
            const q = new URLSearchParams(p);
            q.delete("at");
            q.delete("date");
            return q;
          },
          { replace: true }
        );

        return;
      }

      setParams(
        (p) => {
          const q = new URLSearchParams(p);
          q.set("at", toInput(next).replace("T", " "));
          q.delete("date");
          return q;
        },
        { replace: true }
      );
    }, 60000 / speed);

    return () => clearTimeout(id);
  }, [speed, at, setParams]);

  // いまに戻したら早送りも止める（等速でしか追えない）
  useEffect(() => {
    if (! at) setSpeed(1);
  }, [at]);

  // **初めは横スクロールを中央へ。**線は中心を基準に置いてあるので、
  // 幅が足りないと左端は真っ白で、そこに線があることに気づけない。
  //
  // **1 回だけ。**取り直しごとに戻すと、見ているところから引き剥がされる
  useEffect(() => {
    const el = scroller.current;
    if (! el || centered.current || el.clientWidth === 0) return;

    centered.current = true;
    el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2;
  }, [data]);

  // 位置を進めるための時計。**毎フレームは回さない。**250ms ごとに動かして、
  // あいだは CSS の transition（linear）が埋める
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), TICK);
    return () => clearInterval(id);
  }, []);

  // バスを停留所から取り出して、線の上の位置つきの一覧にする。
  // **線の端に着いてしばらく経ったものは落とす**（居ないものを端に
  // 貼り付けておかない）
  const movers = useMemo(() => {
    // **節が無いデータもある。**手で描いた図は座標で持っているので
    // `nodes` が来ない。ここで止めないと `data.nodes.forEach` で例外が出て、
    // **画面がまるごと真っ白になる**（本番でそうなった）
    if (! data || ! data.nodes) return [];
    const base = new Date(data.as_of.replace(" ", "T")).getTime();
    // 早送り中は、取り直しのあいだも倍率ぶん進める（飛ばさずに滑らせる）
    const elapsedBase = ((now - fetchedAt.current) / 1000) * speed;

    const out = [];
    data.nodes.forEach((node, index) => {
      Object.entries(node.buses).forEach(([bucket, list]) => {
        list.forEach((bus) => {
          const seen = new Date(bus.last_seen_at.replace(" ", "T")).getTime();
          const elapsed = (base - seen) / 1000 + elapsedBase;
          const p = advance(bus.path, bus.path_index, elapsed);
          const fade = edgeOpacity(p.atEdge, p.heldSec);
          if (fade <= 0) return;
          // **向きは進むたびに引き直す。**バケツの鍵はバスが入った
          // ときの向きで、ループ便（21B・21A）は途中で変わる
          const dir = dirOf(bus.path, p.index, bucket);
          // **都市高速を走っているか。**地上の線の上を滑らせると、
          // 停まらない停留所を走っているように見える。判定は時刻表の飛びを
          // 見た backend 側が済ませてある（段の `express`）
          out.push({
            bus,
            dir,
            fade,
            ...laneOf(bus.path, p.index, p.moving, data.spurs),
            express: expressOf(bus.path, p.index, p.moving, data.bypasses),
            ...p,
          });
        });
      });
    });

    // 時刻表にはあるが観測が取れていない便。**推測として混ぜる。**
    // 出さないと「走っていない」と読まれる（監視地点の窓に入らなかっただけ
    // かもしれない）。観測との違いは印で示す
    (data.guesses || []).forEach((g) => {
      const entry = new Date(g.entry_at.replace(" ", "T")).getTime();
      const elapsed = (base - entry) / 1000 + elapsedBase;
      const p = advance(g.path, g.path_index, elapsed);
      const fade = edgeOpacity(p.atEdge, p.heldSec);
      if (fade <= 0) return;
      out.push({
        bus: {
          trip: g.trip,
          bus_number: g.bus_number,
          depot: g.depot,
          bus_source: g.bus_source,
          duty_code: g.duty_code,
          route_no: g.route_no,
          destination: g.destination,
          stop: g.stop,
          last_seen_at: g.entry_at,
          age_minutes: 0,
          delay_minutes: null,
          okure_raw: null,
          node_index: g.node_index,
          next_stops: g.next_stops,
        },
        dir: dirOf(g.path, p.index, g.direction),
        guess: true,
        ...laneOf(g.path, p.index, p.moving, data.spurs),
        express: expressOf(g.path, p.index, p.moving, data.bypasses),
        fade,
        ...p,
      });
    });

    // **同じ線の同じところにいる 2 台は縦に積み分ける。**重ねたままだと
    // 台数を読み違える。線ごとに数える — 本線・都市高速・支線は横位置が
    // 違うので、別の線のバスは重ならない（規則は utils/lineMapStack.js）
    const offsets = stackOffsets(out, ROW);
    offsets.forEach((px, i) => {
      out[i].offset = px;
    });

    return out;
  }, [data, now, speed]);

  /**
   * 手で描いた図のバス。**節を持たないので別に組む。**
   *
   * 進め方は節番号の線とまるごと同じ（`advance` が時刻表の定刻で区間を
   * 割る）。違うのは**位置の出し方だけ**で、節番号ではなく「何番目の区間の
   * どこまで来たか」（index / ratio）を渡し、図の上の座標は
   * `LineDiagramView` が出す — 車線の位置を出すのと同じ場所でないとずれる。
   *
   * 積み分け（重なった 2 台）もあちらでやる。**画素で見ないと重なりが
   * 分からない**（節番号の線は 1 本の軸なので数で比べられた）。
   */
  const diagramMovers = useMemo(() => {
    if (! data || ! data.movers) return [];
    const base = new Date(data.as_of.replace(" ", "T")).getTime();
    // 早送り中は、取り直しのあいだも倍率ぶん進める（飛ばさずに滑らせる）
    const elapsedBase = ((now - fetchedAt.current) / 1000) * speed;

    const out = [];
    data.movers.forEach((bus) => {
      const seen = new Date(bus.last_seen_at.replace(" ", "T")).getTime();
      const p = advance(bus.path, bus.path_index, (base - seen) / 1000 + elapsedBase);
      // 図の外へ走り去る便（`leaves`）は、終点に着いた便より早く消す
      const fade = edgeOpacity(p.atEdge, p.heldSec, bus.leaves);
      // 端に着いて薄れきったものは落とす（居ないものを端に貼り付けない）
      if (fade <= 0) return;
      out.push({ bus, fade, ...p });
    });

    // 時刻表にはあるが観測が取れていない便。**推測として混ぜる**（節番号の
    // 線と同じ）。出さないと「走っていない」と読まれる。**定刻で着いた
    // ことにして**同じ規則で滑らせる。車号は乗番から付いていることがある
    (data.guesses || []).forEach((g) => {
      const entry = new Date(g.entry_at.replace(" ", "T")).getTime();
      const p = advance(g.path, g.path_index, (base - entry) / 1000 + elapsedBase);
      const fade = edgeOpacity(p.atEdge, p.heldSec, g.leaves);
      if (fade <= 0) return;
      out.push({
        bus: {
          ...g,
          last_seen_at: g.entry_at,
          age_minutes: 0,
          delay_minutes: null,
          okure_raw: null,
        },
        guess: true,
        fade,
        ...p,
      });
    });

    return out;
  }, [data, now, speed]);

  if (loading && !data) return <div className="analysis-page">読み込み中…</div>;
  if (error) return <div className="analysis-page linemap-error">{error}</div>;
  if (!data) return null;

  /* **手で描いた図は座標で持っている。**節番号の線とは描き方が違うので、
     ここで分ける（`nodes` ではなく `lines` と `stops` が来る） */
  if (data.lines && data.stops) {
    const shown = diagramMovers.find((m) => m.bus.trip === selected?.trip) || null;

    return (
      <div className="analysis-page linemap">
        <Head
          data={data}
          lines={lines}
          lineKey={lineKey}
          navigate={navigate}
          count={diagramMovers.length}
          guessCount={diagramMovers.filter((m) => m.guess).length}
          fixedAt={at}
          setParams={setParams}
          query={query}
          speed={speed}
          setSpeed={setSpeed}
        />
        <DiagramLegend stops={data.stops} />
        <LineDiagramView data={data} movers={diagramMovers} onSelect={setSelected} />
        {/* タップで開く詳細。**動いているバスに吹き出しを付けない**
            （250ms ごとに動くので読めない）。下に固定して出す */}
        {selected && (
          <BusDetail
            mover={shown}
            here={shown ? <DiagramHere mover={shown} /> : null}
            onClose={() => setSelected(null)}
          />
        )}
        {/* **図の下には何も書かない**（2026-09-23）。観測・推定・推測の
            見分け方は凡例の 1 行にある。詳しくはバスを開くと書いてある */}
      </div>
    );
  }

  // **枝は節より下へ伸びる**ので、節の数だけでは足りない
  const height = (maxExtent(data.nodes, data.spurs) + 1) * ROW;
  // 帯は**最後の節で終わる。**その先は枝しか無いので、伸ばすと回廊が
  // まだ続いているように読める（福工大前から先は三苫・大蔵・雁の巣の枝）
  const railEnd = (data.nodes.length - 1) * ROW + CARD;
  // 幅と帯の位置。**その線にある枝から出す**（入る画面ではスクロールを出さない）
  const box = lineBox(data.bypasses, data.spurs);

  // **日本は左側通行。**下りは進行方向の左が画面の右、上りはその逆。
  // 画面の左から backward, forward の順に並べる
  const railOrder = [...data.directions].reverse();

  return (
    <div className="analysis-page linemap">
      <Head
        data={data}
        lines={lines}
        lineKey={lineKey}
        navigate={navigate}
        count={movers.length}
        guessCount={movers.filter((m) => m.guess).length}
        fixedAt={at}
        setParams={setParams}
        query={query}
        speed={speed}
        setSpeed={setSpeed}
      />

      {/* **横に溢れるぶんをスクロールで辿れるようにする。**幅を決めずに
          置くと、左へ溢れた側に手が届かない */}
      <div className="lm-scroll" ref={scroller}>
        <div
          className="lm-line"
          style={{
            height,
            width: box.width,
            "--lm-axis": `${box.axis}px`,
            // right で置くもの用（右端から数えた帯の位置）
            "--lm-axis-r": `${box.width - box.axis}px`,
            // 帯の下端（最後の節の高さ）
            "--lm-rail-end": `${railEnd}px`,
            ...LANE_VARS,
          }}
        >
          {/* 帯は 1 本の通しで描く。**途中で切ると「そこまで来ない」と
              読めるが、経路は続いている。**ただし最後の節では終わる —
              その先は枝しか無い */}
          <div className="lm-rails" aria-hidden="true">
            {railOrder.map((d) => (
              <span
                key={d.key}
                className={`lm-bar lm-bar--${d.key}`}
                style={{ "--lm-color": DIR_STYLE[d.key]?.color }}
              />
            ))}
          </div>

          {/* 都市高速と支線。**どちらも節ではないので三角も札も無い。**
              停まらない区間を本線の上で滑らせると、通らない停留所を順に
              走っているように見えるので、右の別の線へ逃がす */}
          {((data.bypasses || []).length > 0 || (data.spurs || []).length > 0) && (
            <div className="lm-lanes">
              {(data.spurs || []).map((s) => (
                <Spur key={s.key} spur={s} spurs={data.spurs} />
              ))}
              {(data.bypasses || []).map((b, i) => (
                <Bypass key={i} bypass={b} spurs={data.spurs} />
              ))}
            </div>
          )}

          {/* 停まる向きの三角。帯と同じ左右の並びに合わせる */}
          <div className="lm-tris" aria-hidden="true">
            {data.nodes.map((node, i) =>
              railOrder.map((d, j) => {
                const stops = !node.only || node.only === d.key;
                if (!stops) return null;
                return (
                  <span
                    key={`${i}-${d.key}`}
                    className={`lm-tri lm-tri--${d.key}`}
                    style={{ top: i * ROW + CARD, left: j * 16 }}
                  />
                );
              })
            )}
          </div>

          {/* バス停名。**三角の上に角丸で載せる** */}
          <div className="lm-plates">
            {data.nodes.map((node, i) => (
              <Stop key={i} node={node} directions={data.directions} top={i * ROW + CARD} />
            ))}
          </div>

          {/* 動くバス。**レールの上に絶対位置で置く** */}
          <div className="lm-movers">
            {/* **key は便の身元で固定する。**車号（推測のあいだは無い）で組むと
                確定した瞬間に別要素になり、フェードでの差し替えも確定の合図も
                出せない。1 つの便は 1 つの向きにしか現れない */}
            {movers.map((m) => (
              <Bus
                key={`${m.bus.trip}-${m.dir}`}
                bus={m.bus}
                dir={m.dir}
                at={m.at}
                moving={m.moving}
                fade={m.fade}
                guess={m.guess}
              offset={m.offset}
                lane={m.lane}
                side={m.side}
                altLane={m.altLane}
                spurLane={m.spurLane}
                onSelect={setSelected}
              />
            ))}
          </div>
        </div>
      </div>
      {/* タップで開く詳細。**動いているバスに吹き出しを付けない**
          （250ms ごとに動くので読めない）。下に固定して出す */}
      {selected && (
        <BusDetail
          mover={movers.find(
            (m) => m.bus.trip === selected.trip && m.dir === selected.dir
          )}
          nodes={data.nodes}
          spurs={data.spurs}

          onClose={() => setSelected(null)}
        />
      )}

      <p className="linemap-caveat">
        <strong>停留所にいる表示（塗り）だけが観測です。</strong>
        区間を滑っている表示（輪郭）は、最後に見えた停留所から時刻表の所要で
        進めた<strong>推定</strong>です。遅れが読めていない便ほど外れます。
        所要が分からない区間では進めません。
      </p>
    </div>
  );
}



/** 見出しと凡例 */
function Head({
  data, lines, lineKey, navigate, count, guessCount = 0, fixedAt, setParams, query, speed, setSpeed,
}) {
  return (
    <header className="linemap-head">
      <div className="linemap-head__top">
        <div>
          <h1>{data.name}</h1>
          {data.note && <p className="linemap-note">{data.note}</p>}
        </div>
        {/* **右上に時刻の入口。**始発前・終バス後は走っている便が無いので、
            いまの時刻だけでは画面を確かめられない */}
        <TimeControl
          fixedAt={fixedAt}
          setParams={setParams}
          speed={speed}
          setSpeed={setSpeed}
        />
      </div>

      {/* 線の選び替え。**見ている時刻は引き継ぐ** — 線を変えるたびに
          「いま」へ戻ると、同じ時刻で見比べられない */}
      {lines.length > 1 && (
        <select
          value={lineKey}
          onChange={(e) => navigate(`/analysis/line-map/${e.target.value}${query}`)}
          className="linemap-picker"
          aria-label="見る線"
        >
          {lines.map((l) => (
            <option key={l.key} value={l.key}>
              {l.name}
            </option>
          ))}
        </select>
      )}

      {/* **図の線には向きが無い**（座標で持っているので上下で決まらない）。
          凡例は DiagramLegend のほうが出すので、ここは節番号の線だけ。
          **`hidden` で隠さずに描かない** — `.lm-legend` の `display: flex` が
          `hidden` に勝って、手で描いた図でも出ていた（2026-09-23） */}
      {data.directions && (
      <div className="lm-legend">
        {(data.directions || []).map((d) => (
          <span className="lm-legend__item" key={d.key}>
            <span
              className="lm-legend__bar"
              style={{ background: DIR_STYLE[d.key]?.color }}
            />
            {DIR_STYLE[d.key]?.arrow} {d.name}
          </span>
        ))}
        {/* **迂回線の読み方は断っておく。**外を回る細い線を見て
            「別の路線」と読まれると、居場所を取り違える */}
        {(data.bypasses || []).length > 0 && (
          <span className="lm-legend__note">
            外側の細い線は<strong>{data.bypasses.map((b) => b.label).join("・")}</strong>。
            この区間を通る便は停留所に停まらないので、帯から離して描いています。
            <strong>ランプ名はラベルです</strong>（時刻表に載っていないため、
            ランプのどこまで来たかは分かりません）。
          </span>
        )}
      </div>
      )}

      {/* **いまの時刻ではないことを隠さない。**指定時刻で眺めていると
          気づかないまま「動いていない」と読まれる */}
      {fixedAt && (
        <p className="linemap-fixed">
          <strong>{fixedAt}</strong> を表示中（現在ではありません）
        </p>
      )}

      {/* **時点と台数は 1 行で。**説明は極力短く（2026-09-23）。
          台数は必ず書く — 空なのが「走っていない」のか「拾えていない」のか
          読み手に区別させるため。推測の便は内数で添える */}
      <p className="linemap-count">
        {`${String(data.as_of).slice(5, 16).replace("-", "/")} 時点・${count} 台`}
        {guessCount > 0 && `（うち推測 ${guessCount}）`}
        {data.unplaced > 0 && (
          <span className="linemap-unplaced">
            ・時刻データ無し {data.unplaced}台
          </span>
        )}
      </p>
    </header>
  );
}

/**
 * 都市高速。**右に 1 本だけ引く。**
 *
 * 道は 1 本で、上下で別の道を走るわけではない。向きごとに引くと幅が倍に
 * なって支線の場所が無くなるので、1 本にして**向きはバスの色と矢印で読む**。
 *
 * ## ランプは節ではない
 *
 * 時刻表に載っているのは「停留所が 1 つも無い飛び」だけで、**ランプは
 * 1 件も出てこない**（どの綴りで引いても 0 件。2026-09-16 に確かめた）。
 * だからランプにバスは置かず、名前はラベルとして添えるだけにする。
 *
 * ## 腕の向きで「本線に戻るか」を描き分ける
 *
 * 降りた次が節のランプ（呉服町→蔵本、香椎→福岡女子大前、天神北→天神中央
 * 郵便局前）は、**そこで本線に合流する**ので内側へ腕を引く。
 *
 * 降りた次が支線の停留所のランプ（名島→名島運動公園前、香椎浜→イオン
 * モール香椎浜）は**本線に合流しない。**内側へ腕を引くと「降りてすぐ本線に
 * ショートカット」に見えるので、外側の支線へ引く。
 *
 * ## 札を避けて外側を通す
 *
 * バス停名の札は線の中央に置いてあり、長いものは片側 65px ほど張り出す。
 * 内側に引くと**札ごとに途切れて縞に見える**ので、札の外を通す（幅は CSS の
 * `--lm-bypass-out`）。
 */
function Bypass({ bypass, spurs }) {
  const top = bypass.from * ROW + CARD;
  const height = (bypass.to - bypass.from) * ROW;

  return (
    <>
      {/* 線そのもの。**帯より薄くする** — 同じ濃さだとこちらが本線に
          見える。薄さを重ね掛けしないよう、線はまとめて 1 つに包む */}
      <span className="lm-bypass" aria-hidden="true">
        <span className="lm-bypass__track" style={{ top, height }} />
        {/* ランプごとの腕。**ここで本線か支線と繋がる** */}
        {(bypass.ramps || []).map((r) => (
          <span
            key={r.name}
            className="lm-bypass__arm"
            style={{
              top: r.node * ROW + CARD,
              "--lm-arm-x": `${rampArm(r, spurs).left}px`,
              "--lm-arm-w": `${rampArm(r, spurs).width}px`,
            }}
          />
        ))}
      </span>

      {/* 何の線なのか。**縦書きで線に沿わせる** */}
      <span className="lm-bypass__label" style={{ top: top + 8 }}>
        {bypass.label}
      </span>

      {/* ランプ名。**位置ではなくラベル。**腕を引いた側に置く */}
      {(bypass.ramps || []).map((r) => (
        <span
          key={r.name}
          className="lm-bypass__ramp"
          style={{
            top: r.node * ROW + CARD + 12,
            "--lm-ramp-x": `${rampLabel(r, spurs)}px`,
          }}
          title={
            r.joins
              ? `${bypass.label}の出入口。降りた先はこの停留所（時刻表にランプは無いので、バスはここに置きません）`
              : `${bypass.label}の出入口。降りた先は${r.stop}で、本線には戻りません`
          }
        >
          {r.name}
        </span>
      ))}
    </>
  );
}

/**
 * 支線。**本線には節として並べない。**
 *
 * 都市高速をランプで降りた便や、香椎から分かれる便は回廊の節を通らない。
 * ここを描かないと「降りてすぐ本線にショートカット」に見える。
 *
 * **節ではないので三角も所要も出さない。**名前は線の脇に小さく並べる
 * （札にすると縦に詰まって読めない — 10 停留所を 4 節ぶんの高さに置く）。
 *
 * ## 同じ区間に道が 2 本あることがある
 *
 * 城浜団地⇔イオンモール香椎浜は香椎浜海岸通り回り（119 便）と香椎浜南公園前
 * 回り（102 便）の 2 通り。少ないほうを**もう 1 本外側に引く。**
 *
 * ## 行き止まりの枝もある
 *
 * 下原は香椎から分かれて戻ってこない。`rail` があるところだけ本線と繋ぐ。
 */
function Spur({ spur, spurs }) {
  const y = (node) => node * ROW + CARD;
  // **線は繋がっているところまでしか引かない。**from/to は間隔を決める
  // ための枠で、線の端ではない（名島運動公園前は 24.364 で節 24 ではなく、
  // 下原は 33.333 で節 34 ではない）。余らせると端が宙に浮く
  const ends = spur.stops.map((s) => s.node);
  if (spur.rail !== null && spur.rail !== undefined) ends.push(spur.rail);
  const from = Math.min(...ends);
  const to = Math.max(...ends);
  const top = y(from);
  const height = (to - from) * ROW;
  // **地形に合わせた側に出す。**香椎浜は海側で右、下原は内陸側で左
  const side = `is-${spur.side || "right"}`;
  // 繋がる高さ。節（rail）か、親の支線から分かれるところ（from）
  const joinAt =
    spur.rail !== null && spur.rail !== undefined
      ? spur.rail
      : (spur.host ? spur.from : null);

  return (
    // **車線ごとの横位置はこの枠で配る。**支線が増えても線が重ならない
    <div className="lm-spur-group" style={spurVars(spur.lane)}>
      <span className={`lm-spur ${side}`} aria-hidden="true">
        <span className="lm-spur__track" style={{ top, height }} />
        {/* 繋がるところ。**帯まで、あるいは親の支線まで。**
            どちらも無い枝（行き止まりだけ）には引かない */}
        {joinAt !== null && (
          <span
            className="lm-spur__join"
            style={{
              top: y(joinAt),
              "--lm-join-x": `${joinArm(spur, spurs).x}px`,
              "--lm-join-w": `${joinArm(spur, spurs).w}px`,
            }}
          />
        )}
        {/* 別の道。本道から分かれて、また戻る。**節に繋がる端は帯まで**
            引く（29 番は名島で本線に出るので、本道へは戻らない） */}
        {(spur.alternates || []).map((alt, i) => {
          const at = `is-${alt.lane || "outer"}`;
          const fork = (node, end) => {
            const arm = forkArm(spur, alt, end, spurs);

            return (
              <span
                className={`lm-spur__fork ${at}`}
                style={{
                  top: y(node),
                  "--lm-fork-x": `${arm.x}px`,
                  "--lm-fork-w": `${arm.w}px`,
                }}
              />
            );
          };

          return (
            <React.Fragment key={i}>
              <span
                className={`lm-spur__alt ${at}`}
                // **上向きの道もある**（和白からの道は下から上へ繋がる）。
                // 引き算のままだと高さが負になって、線がまるごと消える
                style={{
                  top: y(Math.min(alt.from, alt.to)),
                  height: Math.abs(alt.to - alt.from) * ROW,
                }}
              />
              {fork(alt.from, {
                rail: alt.from_rail, host: alt.from_host, alt: alt.from_alt,
              })}
              {fork(alt.to, {
                rail: alt.to_rail, host: alt.to_host, alt: alt.to_alt,
              })}
            </React.Fragment>
          );
        })}
      </span>

      {spur.stops.map((s) => (
        <SpurStop key={s.label} stop={s} side={side} top={y(s.node)} />
      ))}

      {(spur.alternates || []).flatMap((alt) =>
        alt.stops.map((s) => ({ ...s, lane: alt.lane || "outer" }))
      ).map((s) => (
        <SpurStop key={s.label} stop={s} side={side} top={y(s.node)} alt={s.lane} />
      ))}
    </div>
  );
}

/**
 * 支線の停留所。**節ではないので三角も所要も出さない。**
 *
 * 名前は線の外側へ出す（内側は都市高速とバスが通る）。ただし**分岐の
 * 停留所は内側** — 外側には分岐の腕が伸びるので、重なって読めない。
 */
function SpurStop({ stop, side, top, alt = null }) {
  return (
    <span
      className={
        "lm-spur__stop" +
        (alt ? ` lm-spur__stop--alt is-${alt}` : "") +
        ` ${side}` +
        (stop.name_side === "inner" ? " is-name-inner" : "") +
        // **ランプの腕がこの高さを横切る。**名前を少し上へ逃がす
        (stop.ramp ? " is-ramped" : "")
      }
      style={{ top }}
    >
      <span className="lm-spur__dot" aria-hidden="true" />
      <span className="lm-spur__name">{stop.label}</span>
    </span>
  );
}

/**
 * バス停名の札。**三角の上に角丸で載せる。**
 *
 * 片方向しか停まらない停留所の注記は**名前の右横に並べる。**下に小さく
 * 置くと読み飛ばされるが、ここは「帯は通っているのに停まらない」という
 * 読み違えやすいところなので目に入る位置に出す。
 *
 * **のりばの数は出さない。**向きで乗り場が分かれていることは、この画面で
 * 見たいこと（どこに何がいるか）とは関係がない。
 */
function Stop({ node, directions, top }) {
  const only = node.only
    ? directions.find((d) => d.key === node.only)
    : null;

  return (
    <div className="lm-plate" style={{ top }}>
      <span className="lm-plate__box">
        <span className="lm-plate__name">{node.label}</span>
        {only && (
          <span className={`lm-plate__only lm-plate__only--${only.key}`}>
            {only.name}のみ停車
          </span>
        )}
      </span>
      {node.minutes_to_next != null && (
        <span className="lm-plate__min">{node.minutes_to_next}分</span>
      )}
    </div>
  );
}

/**
 * 線の上の 1 台。
 *
 * **塗りは停留所にいるときだけ。**区間を滑っているあいだは輪郭にする。
 * 動いて見えることと、位置が分かっていることは別なので、そこを形で分ける。
 *
 * ## 観測が更新されて位置がズレたら、滑らせずに差し替える
 *
 * 巡回のたびに新しい観測が来る。推定で進めていた位置と実際の位置は
 * たいていズレる（遅れが読めていない便ほど大きい）。
 *
 * **そのズレを transform の遷移で埋めてはいけない。**実際には起きていない
 * 移動を描くことになる。とくに後ろへ戻る補正は、バスが逆走したように見える。
 *
 * だから**消してから、新しい位置で出す。**フェードのあいだは位置を動かさない
 * （`is-snap` で transform の遷移を切る）。ズレたことが見た目に出るほうが、
 * なめらかに嘘をつくより良い。
 */
function Bus({
  bus, dir, at, moving, fade, guess, offset, lane, side, altLane, spurLane,
  onSelect,
}) {
  // 表示中の位置と、出しているかどうか。**at をそのまま使わない**
  const [shown, setShown] = useState({ at, visible: true, snap: false });
  const latest = useRef(at);
  const prev = useRef({ seen: bus.last_seen_at, at });
  const timers = useRef([]);

  // **確定した合図と、遅れが動いた合図。**どちらも一瞬だけ出す
  const [flash, setFlash] = useState(null);
  const was = useRef({ guess, delay: bus.delay_minutes });

  // **線を移ったあいだだけ長く滑らせる。**位置を進める 250ms のままだと
  // 500px を一瞬で飛ぶので、どのバスがどの線へ移ったのか追えない
  const [moving2, setMoving2] = useState(false);
  const lastLane = useRef(`${lane}/${side}/${altLane}/${spurLane}`);

  latest.current = at;

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  useEffect(() => {
    // 差し替え中は触らない（フェードを途切れさせない）
    if (timers.current.length) return;

    const seenChanged = prev.current.seen !== bus.last_seen_at;
    const jumped = Math.abs(at - prev.current.at) > SWAP_THRESHOLD;

    if (seenChanged && jumped) {
      setShown((s) => ({ ...s, visible: false }));       // まず消す

      timers.current.push(
        setTimeout(() => {
          // 消えているあいだに位置を差し替える。**遷移は切る**
          setShown({ at: latest.current, visible: true, snap: true });
          prev.current = { seen: bus.last_seen_at, at: latest.current };
          timers.current.push(
            setTimeout(() => {
              setShown((s) => ({ ...s, snap: false }));
              timers.current = [];
            }, 50)
          );
        }, SWAP_MS)
      );

      return;
    }

    prev.current = { seen: bus.last_seen_at, at };
    setShown({ at, visible: true, snap: false });
  }, [at, bus.last_seen_at]);

  useEffect(() => {
    const key = `${lane}/${side}/${altLane}/${spurLane}`;
    if (lastLane.current === key) return undefined;

    lastLane.current = key;
    setMoving2(true);
    const id = setTimeout(() => setMoving2(false), LANE_MS);

    return () => clearTimeout(id);
  }, [lane, side, altLane, spurLane]);

  // 推測 → 確定、と遅れの増減。**気づけるように一瞬だけ強く出す**
  useEffect(() => {
    const w = was.current;
    let next = null;

    if (w.guess && ! guess) {
      next = "confirm";
    } else if (w.delay !== bus.delay_minutes) {
      const from = w.delay ?? 0;
      const to = bus.delay_minutes ?? 0;
      next = to > from ? "worse" : "better";
    }

    was.current = { guess, delay: bus.delay_minutes };
    if (! next) return;

    setFlash(next);
    const id = setTimeout(() => setFlash(null), FLASH_MS);
    return () => clearTimeout(id);
  }, [guess, bus.delay_minutes]);

  // 観測の古さで薄くし、線の端に着いたぶんと差し替えのぶんを掛ける
  const opacity =
    (1 - Math.min(bus.age_minutes / 30, 1) * 0.5) * fade * (shown.visible ? 1 : 0);
  const delay = delayBadge(bus.delay_minutes);
  const dirClass = dir === "forward" ? "lm-mover--right" : "lm-mover--left";
  // 左側通行。下りは線の右、上りは線の左（本線でも支線でも同じ）
  const x = busX(lane, side, altLane, spurLane, dir);
  // **本線から外れているあいだは、その線の脇へ移す。**帯の脇に置いた
  // ままだと、通らない停留所を順に走っているように見える。
  // 都市高速と支線は右に 1 本ずつなので、上りもそちら側へ出る

  return (
    <span
      className={
        `lm-mover ${dirClass}` +
        (lane === "rail" ? "" : ` is-${lane} is-${side}`) +
        (altLane ? ` is-${altLane}` : "") +
        (moving2 ? " is-lane" : "") +
        (shown.snap ? " is-snap" : "")
      }
      // **縦も横も 1 つの transform で動かす。**別々の性質で動かすと、
      // 線を移るときに片方だけ飛ぶ。上りの本線だけは枠の右端を中心に
      // 合わせるので -100% から引く
      style={{
        transform: `translate(${x}, ${shown.at * ROW + CARD + offset}px)`,
        opacity,
      }}
    >
      {/* **推測の印は線の外側に置く。**車号の側に置くと、無い車号の跡と
          見分けが付かない。
          **幅は 1 文字ぶんのまま、字を横に潰して 2 文字入れる**
          （scaleX。1 文字だけだと「推」が何を指すか読めない） */}
      {guess && (
        <span
          className="lm-guess"
          title="時刻表ではもう出ている便。位置は取れていないので、いる場所は推測です"
        >
          <span className="lm-squeeze">推測</span>
        </span>
      )}

      <button
        type="button"
        className={
          `lm-bus lm-bus--${dir} ${moving ? "is-moving" : "is-at"}` +
          (guess ? " is-guess" : "") +
          (flash ? ` flash-${flash}` : "")
        }
        style={{ "--lm-color": DIR_STYLE[dir]?.color }}
        onClick={() => onSelect({ bus_number: bus.bus_number, dir, trip: bus.trip })}
        title="タップで詳しく"
      >
        <span className="lm-bus__arrow">{DIR_STYLE[dir]?.arrow}</span>
        {/* 営業所の頭 1 文字と車号を**1 つの塊にする。**
            上り側は枠の中身を反転させて矢印を線側へ向けているので、別々の
            要素にすると「1815ア」の順に入れ替わってしまう。塊にすれば
            どちらの向きでも「ア1815」と読める。
            車号が分からないときは「？」（日本語で書くと車号より長くて枠に
            収まらない）。
            **車号の出どころ（目撃か乗番からの予測か）は線の上には出さない。**
            この枠には「推測」の印が付いていて、観測でないことは分かる */}
        <span className="lm-bus__id">
          {bus.depot && (
            <span className="lm-bus__depot" title={bus.depot}>
              {Array.from(bus.depot)[0]}
            </span>
          )}
          <span className="lm-bus__no">{bus.bus_number || "？"}</span>
        </span>
        {/* 行先番号。**箱の幅は 1.5em に固定**（線の上に並んだとき列を
            揃えるため）。はみ出すぶんだけ横に潰し、収まるものは中央に置く。
            **行先番号を持たない系統は「無番」と書く** — にしてつ自身が
            そう呼ぶ（「無番 香椎花園行」）。空けると読み手が補ってしまう */}
        <span className={`lm-bus__route${bus.route_no ? "" : " is-none"}`}>
          <span
            className="lm-fit"
            style={{ transform: `scaleX(${routeScale(bus.route_no || "無番")})` }}
          >
            {bus.route_no || "無番"}
          </span>
        </span>
        {/* 遅れのバッジ。**「運行中」だけ 3 文字あるので横に潰して 2 文字ぶんに
            収める。**数字のバッジと幅が揃わないと列がばらける */}
        <span className={`lm-badge ${delay.cls}`} title={delay.label}>
          {delay.cls === "d-unknown" ? (
            <span className="lm-squeeze lm-squeeze--3to2">{delay.text}</span>
          ) : (
            delay.text
          )}
        </span>
      </button>
    </span>
  );
}


/**
 * タップして開く 1 台の詳細。**省略しない。**
 *
 * 線の上のバッジは場所がないので詰めてある（`ア` `1925` `+3`）。ここでは
 * 略さずに出す（`アイランド1925` `23N蔵本行き` `3分遅れ`）。**色は線の上と
 * 揃える** — 同じものを見ていると分かるように。
 *
 * 動いているバスに吹き出しを付けない。250ms ごとに動くので読めない。
 * 下に固定して出し、データが更新されれば中身も追従する。
 *
 * ## 観測の事実も併せて出す
 *
 * 線の上では位置が塗りか輪郭かでしか言えていない。ここでは**いつ・どこで
 * 見えたのか**を書く。いまの位置が推定なら、そう書く。
 *
 * ## 「現在地」の言い方だけ図によって違う
 *
 * 節番号の線は節の名前で言える（`nodes`）。手で描いた図は節を持たないので、
 * 呼ぶ側が `here` で渡す。**それ以外はまるごと同じ** — 同じものを見ている
 * と分かるように、車号も色も言い方も揃える。
 */
function BusDetail({ mover, nodes, spurs, here = null, onClose }) {
  // 消えた（端に着いて薄れきった・窓から外れた）ときは閉じる
  useEffect(() => {
    if (! mover) onClose();
  }, [mover, onClose]);

  if (! mover) return null;

  const { bus, dir, moving, at, guess, lane, express } = mover;
  const delay = delayBadge(bus.delay_minutes);
  const style = DIR_STYLE[dir];

  // **推定で進んだぶんだけ「次」を送る。**最後に見えた停留所の次を出し続けると、
  // すでに通り過ぎた停留所を「次」と言うことになる。
  // **数えるのは節ではなく、その便が停まる並びのほう**（便は節を飛ばす）。
  // 段の番号で数える — 位置の大小で数えると、**同じ停留所へ戻るループ便**で
  // 数え違える（21B は雁の巣で折り返す）
  const passed = Math.max(0, (mover.index ?? 0) - (bus.path_index ?? 0));
  const next = (bus.next_stops || [])[passed] || null;

  return (
    <div className="lm-sheet" style={{ "--lm-color": style?.color }}>
      <button type="button" className="lm-sheet__close" onClick={onClose} aria-label="閉じる">
        ×
      </button>

      <div className="lm-sheet__head">
        <span className="lm-sheet__arrow">{style?.arrow}</span>
        {/* 車号。**出どころで書き分ける。**位置が推測でも、車号は事実である
            ことがある（その便を目撃している場合）。混ぜない */}
        <span className="lm-sheet__bus">
          {bus.bus_number
            ? `${bus.depot || ""}${bus.bus_number}`
            : "車号は分かっていません"}
        </span>
        {bus.bus_source === "forecast" && (
          <span className="lm-sheet__pred">
            乗番{bus.duty_code || ""}からの予測
          </span>
        )}
      </div>

      {(bus.route_no || bus.destination) && (
        <p className="lm-sheet__trip">
          {/* にしてつ自身の呼び方に揃える（「無番 香椎花園行」）。
              **行先番号を持たない急行がある**ので、そこは「急行」と書く
              （新宮・大蔵まわりの都市高速便。判定は API の isKyuko） */}
          {bus.route_no || (bus.kyuko ? "急行" : "無番")}
          {bus.destination ? ` ${bus.destination}行き` : ""}
          {/* **行先は方面表示で、終点ではない。**ループ便では折り返した
              あとも「雁の巣・和白行き」に見えるので、終点を添える */}
          {viaOnly(bus) && (
            <span className="lm-sheet__terminal">
              {`終点 ${bus.terminal_name}`}
            </span>
          )}
        </p>
      )}

      {/* 遅れと、次に停まる停留所を横に並べる。**色はバッジの段をそのまま**
          使い、言い方だけ略さない */}
      <div className="lm-sheet__row">
        <span className={`lm-sheet__delay ${delay.cls}`}>{delay.label}</span>
        {next && (
          <span className="lm-sheet__next">
            次は <strong>{next.name}</strong>
            <span className="lm-sheet__next-time">{next.time}</span>
            {/* **定刻であることを隠さない。**遅れを足した予想時刻ではない */}
            <span className="lm-sheet__next-note">定刻</span>
          </span>
        )}
      </div>

      {/* **推測であることをここでも書く。**線の上は印だけなので、開いたら
          言葉で言う。車号が事実なのか予測なのかも分けて書く */}
      {guess && (
        <p className="lm-sheet__guessnote">
          <strong>時刻表上では発車済みの便ですが、運行情報が取得できていません。</strong>
          {bus.bus_source === "observed" && "車号はこの便の目撃から判明しています。"}
          {bus.bus_source === "forecast" && "車号も運用解析の乗番からの予測となります。"}
          {! bus.bus_source && "この便は乗番に属さないため、車号は不明です。"}
        </p>
      )}

      <dl className="lm-sheet__facts">
        {/* **推測の便は何も取得していない。**「最終取得地」と書くと、その
            停留所で見えたように読める。時刻表の通過（定刻）として書く */}
        {guess ? (
          <>
            <dt>時刻表上の通過</dt>
            <dd>
              {bus.stop} / {String(bus.last_seen_at).slice(11, 16)}
              <span className="lm-sheet__next-note">定刻</span>
            </dd>
          </>
        ) : (
          <>
            <dt>最終取得地</dt>
            <dd>
              {bus.stop} / {bus.last_seen_at}
              （{bus.age_minutes}分前）
            </dd>
          </>
        )}
        <dt>現在地</dt>
        <dd>
          {/* **節を持たない図は呼ぶ側が言う。**節の名前では言えない */}
          {here ?? (moving && express ? (
            // **高速の上はランプのあいだで言う。**ランプは時刻表に無いので、
            // そのどこまで来たかは言えない
            <strong className="lm-sheet__guess">
              {express.bypass.label}を走行中（推定）
              <span className="lm-sheet__bypass">
                {[express.from, express.to].filter(Boolean).join("〜") || "区間は不明"}
              </span>
            </strong>
          ) : moving && lane === "spur" ? (
            // 支線（香椎浜まわり）。**節ではないので節の名前では言えない**
            <strong className="lm-sheet__guess">
              バス停間を走行中（推定）
              <span className="lm-sheet__bypass">{spurName(spurs, lane, at)}</span>
            </strong>
          ) : moving ? (
            <strong className="lm-sheet__guess">バス停間を走行中（推定）</strong>
          ) : lane === "spur" ? (
            // 支線の停留所。**本線の節ではないので、観測した名前をそのまま出す**
            <>
              {bus.stop}
              <span className="lm-sheet__bypass">
                {spurLabelOf(spurs)}まわり（本線の外）
              </span>
            </>
          ) : (
            <>
              {nodes?.[Math.round(at)]?.label ?? "—"}
              {/* **推定で先へ送ったぶんは印を付ける。**最後に見えた停留所に
                  いるなら観測だが、そこから進めた位置は推定 */}
              {Math.abs(at - bus.node_index) > 0.01 && (
                <strong className="lm-sheet__guess">（推定）</strong>
              )}
            </>
          ))}
        </dd>
      </dl>
    </div>
  );
}

/**
 * 手で描いた図での「現在地」。**節の名前では言えない。**
 *
 * 図は (行, 列) で持っているので、節番号の線のように「節 28 = 香椎」とは
 * 言えない。言えるのは**停まる並びのどこか**だけなので、そこから出す。
 *
 * 都市高速の区間は**ランプのあいだ**としか言えない（ランプは時刻表に無い
 * ので、そのどこまで来たかは分からない）。
 */
function DiagramHere({ mover }) {
  const { bus, moving, index, guess } = mover;
  // **区間の印。**その段から次の段までが都市高速かどうか
  const express = !! (bus.path || [])[index]?.express;
  // 段は停まる並びと同じ番号なので、`next_stops` と突き合わせて名前が出る
  const at = index === bus.path_index
    ? bus.stop
    : (bus.next_stops || [])[index - bus.path_index - 1]?.name;

  if (moving) {
    return (
      <strong className="lm-sheet__guess">
        {express ? "都市高速を走行中（推定）" : "バス停間を走行中（推定）"}
        {at && <span className="lm-sheet__bypass">{at}を出たところ</span>}
      </strong>
    );
  }

  return (
    <>
      {at ?? "—"}
      {/* **推定で先へ送ったぶんは印を付ける。**最後に見えた停留所に
          いるなら観測だが、そこから進めた位置は推定。**推測の便は
          はじめから観測が無い**ので、どこにいても推定 */}
      {(guess || index !== bus.path_index) && (
        <strong className="lm-sheet__guess">（推定）</strong>
      )}
    </>
  );
}

/** 支線の名前（1 本しか無いので最初のもの） */
function spurLabelOf(spurs) {
  return (spurs || [])[0]?.label ?? "支線";
}

/**
 * 支線のどのあたりを走っているか。**節ではないので番号では言えない。**
 * 前後の停留所の名前で挟んで言う。
 */
function spurName(spurs, lane, at) {
  const spur = (spurs || [])[0];
  if (! spur) return "";

  const sorted = [...spur.stops].sort((a, b) => a.node - b.node);
  const before = sorted.filter((s) => s.node <= at).at(-1);
  const after = sorted.find((s) => s.node > at);

  return [before?.label, after?.label].filter(Boolean).join("〜") ||
    `${spur.label}まわり`;
}

/**
 * 見る時刻を変える入口。**右上に小さく置いて、押したら出る。**
 *
 * **始発前・終バス後は走っている便が無い。**いまの時刻しか見られないと、
 * 画面が空なのが「拾えていない」のか「そもそも走っていない」のか確かめ
 * られない。過去日を眺めるときも同じ口を使う。
 *
 * 時刻は URL に載せる。**その時刻のまま共有・ブックマークできる。**
 */
function TimeControl({ fixedAt, setParams, speed, setSpeed }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(() => toInput(fixedAt));
  const box = useRef(null);

  useEffect(() => setValue(toInput(fixedAt)), [fixedAt]);

  // 外を押したら閉じる。Esc でも閉じる
  useEffect(() => {
    if (! open) return;
    const outside = (e) => {
      if (box.current && ! box.current.contains(e.target)) setOpen(false);
    };
    const esc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", outside);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", outside);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  const apply = (next) => {
    // 時刻を選び直したら早送りは止める（どこを見ているのか分からなくなる）
    setSpeed(1);
    setValue(toInput(next));
    setParams((p) => {
      const q = new URLSearchParams(p);
      q.set("at", toInput(next).replace("T", " "));
      // 運行日はサーバが at から出す（04:00 始まり）ので消しておく
      q.delete("date");
      return q;
    });
  };

  const nudge = (minutes) => {
    const base = new Date(value.replace(" ", "T"));
    base.setMinutes(base.getMinutes() + minutes);
    apply(base);
  };

  // 早送りは段で回す。**押すごとに 1 段上がり、最後でまた等速に戻る。**
  // 等速のうちに押したら、いまの時刻を起点に固定してから始める
  const stage = SPEEDS.indexOf(speed) + 1;
  const cycle = () => {
    const next = stage >= SPEEDS.length ? 1 : SPEEDS[stage];
    if (next > 1 && ! fixedAt) {
      const now = new Date();
      now.setSeconds(0, 0);
      setValue(toInput(now));
      setParams((p) => {
        const q = new URLSearchParams(p);
        q.set("at", toInput(now).replace("T", " "));
        q.delete("date");
        return q;
      });
    }
    setSpeed(next);
  };

  return (
    <div className="lm-time" ref={box}>
      <button
        type="button"
        className={`lm-time__trigger${fixedAt ? " is-fixed" : ""}`}
        onClick={() => setOpen((o) => ! o)}
        aria-label="時刻を変える"
        aria-expanded={open}
      >
        🕘 {fixedAt ? fixedAt.slice(5, 16) : "いま"}
      </button>

      {open && (
        <div className="lm-time__pop">
          {/* **山記号の数で刻みの大きさを示す。**時刻を真ん中に置いて、
              左が前・右が後ろ。意味は title に出す */}
          <div className="lm-time__row lm-time__row--bar">
            {[
              [-30, "<<<"],
              [-5, "<<"],
              [-1, "<"],
            ].map(([m, label]) => (
              <button key={m} type="button" onClick={() => nudge(m)} title={`${-m}分前`}>
                {label}
              </button>
            ))}

            <input
              type="datetime-local"
              step="60"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              aria-label="見る時刻"
            />

            {[
              [1, ">"],
              [5, ">>"],
              [30, ">>>"],
            ].map(([m, label]) => (
              <button key={m} type="button" onClick={() => nudge(m)} title={`${m}分後`}>
                {label}
              </button>
            ))}
          </div>

          <div className="lm-time__row">
            <button type="button" className="lm-time__go" onClick={() => apply(value)}>
              この時刻を見る
            </button>
            <button
              type="button"
              disabled={! fixedAt}
              onClick={() => {
                setSpeed(1);
                setParams((p) => {
                  const q = new URLSearchParams(p);
                  q.delete("at");
                  q.delete("date");
                  return q;
                });
                setOpen(false);
              }}
            >
              いまに戻す
            </button>

            {/* **早送り。**押すごとに段が上がり、最後でまた等速に戻る。
                塗った三角の数が段（▷▷▷ が等速、▶▶▶ が最速） */}
            <button
              type="button"
              className={`lm-time__speed${speed > 1 ? " is-on" : ""}`}
              onClick={cycle}
              title={
                speed > 1
                  ? `×${speed} で早送り中（押すと ${
                      stage >= SPEEDS.length ? "等速に戻る" : `×${SPEEDS[stage]}`
                    }）`
                  : `早送り（押すと ×${SPEEDS[0]}）。いまの時刻を起点に固定します`
              }
              aria-label="早送り"
            >
              <span className="lm-time__marks" aria-hidden="true">
                {SPEEDS.map((v, i) => (i < stage ? "▶" : "▷")).join("")}
              </span>
              {speed > 1 && <span className="lm-time__rate">×{speed}</span>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * datetime-local が読める形（ローカル時刻）にする。
 *
 * **Date でも文字列でも受ける。**文字列のときだけ空白を T に直す
 * （Date を String() に通すと "Tue Sep 15 ..." になり、置換で壊れる）。
 */
function toInput(value) {
  let d;
  if (value instanceof Date) {
    d = value;
  } else if (value) {
    d = new Date(String(value).replace(" ", "T"));
  } else {
    d = new Date();
  }
  const pad = (n) => String(n).padStart(2, "0");

  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}
